import apiClient from 'helpers/api-client'
import { generateShortUuid } from 'helpers/generate/uuid'
import { API_FINANCIAL } from 'routes/apis'
import { DataResponse } from '~/@types/commons'
import {
  FinancialApproval,
  FinancialApprovalActionParams,
  FinancialApprovalActionResult,
  UpdateBankAccountParams,
  UpdateBankAccountResponse,
  WithdrawParams,
  WithdrawResponse,
} from './interface'

// Fetch a financial-approval request by its token (from the email link's URL
// param). Read-only — safe to run on page load.
export const getApprovalByToken = async (
  token: string,
): Promise<FinancialApproval> => {
  const res = await apiClient.get<DataResponse<FinancialApproval>>(
    API_FINANCIAL.approvals.action,
    { params: { token } },
  )
  return res.data.data
}

// Commit the approve/deny decision for a request. The one-time token is the
// step-up (no OTP); a bad action value returns 422 invalid_action.
export const submitApprovalAction = async (
  params: FinancialApprovalActionParams,
): Promise<FinancialApprovalActionResult> => {
  const res = await apiClient.post<
    DataResponse<FinancialApprovalActionResult>
  >(API_FINANCIAL.approvals.action, params)
  return res.data.data
}

// Owner: processed immediately (status: 'COMPLETED'). Anyone else: goes to
// Owner-approval instead (status: 'PENDING') — see WithdrawResponse. A repeat
// call while one is already pending fails 409 CONFLICT (verified against the
// live endpoint; an earlier note here guessed `approval_already_pending`).
//
// idempotent-key is generated per call, not read off apiClient's shared
// defaults: a retried request must reuse the same key (so the BE can dedupe
// it), but two independent withdrawals must never accidentally share one.
export const requestWithdrawal = async (
  params: WithdrawParams,
): Promise<WithdrawResponse> => {
  const res = await apiClient.post<DataResponse<WithdrawResponse>>(
    API_FINANCIAL.withdraw,
    params,
    { headers: { 'idempotent-key': generateShortUuid() } },
  )
  return res.data.data
}

// Owner: processed immediately (status: 'EXECUTED'). Anyone else: goes to
// Owner-approval instead (status: 'PENDING') — see UpdateBankAccountResponse.
// Like withdraw above, a repeat call while one is already pending fails 409
// CONFLICT.
export const updateBankAccount = async (
  params: UpdateBankAccountParams,
): Promise<UpdateBankAccountResponse> => {
  const res = await apiClient.post<DataResponse<UpdateBankAccountResponse>>(
    API_FINANCIAL.bankAccount,
    params,
    { headers: { 'idempotent-key': generateShortUuid() } },
  )
  return res.data.data
}
